/* Recycle bin: deleted students / teachers / staff kept for 30 days, with
 * one-click restore. Admin only. */
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Tabs, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  ArrowCounterClockwise, Trash as TrashIcon, GraduationCap, ChalkboardTeacher, Users, Clock,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const ENTITY_META = {
  student: { icon: GraduationCap, label: 'Élève', color: 'text-[#0052FF]' },
  teacher: { icon: ChalkboardTeacher, label: 'Enseignant', color: 'text-emerald-600' },
  staff: { icon: Users, label: 'Personnel', color: 'text-amber-600' },
};

export default function Trash() {
  const [items, setItems] = useState([]);
  const [retention, setRetention] = useState(30);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/trash');
      setItems(data.items || []);
      setRetention(data.retention_days || 30);
    } catch {
      toast.error('Impossible de charger la corbeille');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const restore = async (it) => {
    setBusyId(it.id);
    try {
      await api.post(`/trash/${it.id}/restore`);
      toast.success(`${it.label} restauré(e)`);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restauration impossible');
    } finally { setBusyId(''); }
  };

  const purge = async (it) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT ${it.label} ? Cette action est irréversible.`)) return;
    setBusyId(it.id);
    try {
      await api.delete(`/trash/${it.id}`);
      toast.success('Supprimé définitivement');
      setItems((prev) => prev.filter((x) => x.id !== it.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suppression impossible');
    } finally { setBusyId(''); }
  };

  const shown = filter === 'all' ? items : items.filter((i) => i.entity_type === filter);
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="trash-page">
      <div>
        <h1 className="text-3xl font-outfit font-bold text-[#0F172A] flex items-center gap-2">
          <TrashIcon size={28} weight="duotone" className="text-[#0052FF]" /> Corbeille
        </h1>
        <p className="text-sm text-[#64748B] mt-1">
          Les élèves, enseignants et personnels supprimés sont conservés {retention} jours,
          puis effacés automatiquement. Restaurez-les en un clic.
        </p>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList data-testid="trash-filter-tabs">
          <TabsTrigger value="all" data-testid="trash-tab-all">Tout ({items.length})</TabsTrigger>
          <TabsTrigger value="student" data-testid="trash-tab-student">Élèves</TabsTrigger>
          <TabsTrigger value="teacher" data-testid="trash-tab-teacher">Enseignants</TabsTrigger>
          <TabsTrigger value="staff" data-testid="trash-tab-staff">Personnel</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="rounded-lg border border-[#E2E8F0] bg-white">
        {loading ? (
          <p className="p-8 text-center text-sm text-[#94A3B8]">Chargement…</p>
        ) : shown.length === 0 ? (
          <div className="p-12 text-center" data-testid="trash-empty">
            <TrashIcon size={40} className="mx-auto text-[#CBD5E1] mb-3" weight="duotone" />
            <p className="text-sm text-[#64748B]">La corbeille est vide.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Supprimé le</TableHead>
                <TableHead>Par</TableHead>
                <TableHead>Expire dans</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((it) => {
                const meta = ENTITY_META[it.entity_type] || ENTITY_META.staff;
                const Icon = meta.icon;
                return (
                  <TableRow key={it.id} data-testid={`trash-row-${it.id}`}>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1.5 text-sm ${meta.color}`}>
                        <Icon size={16} weight="duotone" /> {it.entity_label || meta.label}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">
                      {it.label}
                      {it.extra ? <span className="text-[#94A3B8] text-xs ml-1">· {it.extra}</span> : null}
                    </TableCell>
                    <TableCell className="text-sm text-[#64748B]">{fmtDate(it.deleted_at)}</TableCell>
                    <TableCell className="text-sm text-[#64748B]">{it.deleted_by || '—'}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-xs font-medium ${it.days_left <= 5 ? 'text-red-600' : 'text-[#64748B]'}`}>
                        <Clock size={14} /> {it.days_left} j
                      </span>
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        size="sm" onClick={() => restore(it)} disabled={busyId === it.id}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`trash-restore-${it.id}`}
                      >
                        <ArrowCounterClockwise size={15} className="mr-1.5" /> Restaurer
                      </Button>
                      <Button
                        size="sm" variant="ghost" onClick={() => purge(it)} disabled={busyId === it.id}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`trash-purge-${it.id}`}
                      >
                        <TrashIcon size={15} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
